import { gql, useEnhancedMutation, useEnhancedQuery } from "@uplift-ltd/apollo";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { EventIdType } from "__generated__/globalTypes";
import Button from "../Button";
import { Select } from "../Form";
import { OptionItem } from "../Form/types";
import {
  EventCertificatesQueryQuery as EventCertificatesQuery,
  EventCertificatesQueryVariables,
  SetLiveCeCertificateMutation,
  SetLiveCeCertificateVariables,
} from "./__generated__/CertificateSelect";

const EVENT_CERTIFICATES = gql`
  query EventCertificatesQuery($id: ID!, $idType: EventIdType!) {
    event(id: $id, idType: $idType) {
      id
      eventFields {
        certificates {
          ... on Certificate {
            id
            title
          }
        }
      }
    }
  }
`;

const SET_CERTIFICATE = gql`
  mutation SetLiveCeCertificate($eventId: ID!, $certificateId: ID!) {
    setLiveCeCertificate(input: { eventId: $eventId, certificateId: $certificateId }) {
      success
    }
  }
`;

interface CertificateSelectProps {
  eventId: string;
  courseRefetchHandler: () => void;
}

const CertificateSelect = ({ eventId, courseRefetchHandler }: CertificateSelectProps) => {
  const { data, error, loading, refetch } = useEnhancedQuery<
    EventCertificatesQuery,
    EventCertificatesQueryVariables
  >(EVENT_CERTIFICATES, {
    variables: {
      id: eventId,
      idType: EventIdType.ID,
    },
  });

  useEffect(() => {
    if (!loading && !error && !data?.event?.eventFields?.certificates) {
      refetch();
    }
  }, [loading, data, error, refetch]);

  const [certificateId, setCertificateId] = useState("");

  const [setCertificate] = useEnhancedMutation<
    SetLiveCeCertificateMutation,
    SetLiveCeCertificateVariables
  >(SET_CERTIFICATE, {}, { auth: false });

  const onChange: React.ChangeEventHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setCertificateId(event.currentTarget.value);
  };

  const save = useCallback(async () => {
    const certId = certificateId;
    setCertificateId("");
    const result = await setCertificate({
      variables: {
        eventId,
        certificateId: certId,
      },
    });
    if (result.data?.setLiveCeCertificate?.success) {
      courseRefetchHandler();
    }
  }, [setCertificate, eventId, certificateId, courseRefetchHandler]);

  const certificates = data?.event?.eventFields?.certificates || [];
  const options: OptionItem[] = certificates.map(certificate => {
    return {
      label: certificate?.title || "",
      value: certificate?.id || "",
    };
  });

  return (
    <div>
      <div>
        <Select onChange={onChange} options={options} placeholder="Please choose a certificate…" />
      </div>
      <div>
        <Button onClick={save} disabled={!certificateId}>
          Save and continue
        </Button>
      </div>
    </div>
  );
};

export default CertificateSelect;
